import React from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import I18n from 'i18n-js';
import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import { isDev, isProduction } from 'common/utils/environment-util';
import { getLocaleFromBrowserLanguage } from 'grab-locale';
import { getUserFeatureFlagsAsync } from 'helper/get-user-feature-flags-helper';
import Storage from 'grab-storage';
import 'grab-ui/dist/theme.less';
import Store from './store';
import AppV2 from './AppV2';
import './index.less';
import {
  APP_BASE_URL_NAME,
  STORAGE_LOCALE,
  SUPPORTED_LOCALES,
} from './constants/app-constants';
import { GLOBAL_INIT_CLIENT } from './actions/app-actions';

window.Grab = {
  config: {},
  router: {},
  storage: Storage,
};

const getLocale = () =>
  window.localStorage.getItem(STORAGE_LOCALE) ||
  getLocaleFromBrowserLanguage({
    defaultLocale: 'en',
    availableLocales: SUPPORTED_LOCALES,
    navigator: window.navigator,
  });

const renderAppV2 = () => {
  Store.dispatch({ type: GLOBAL_INIT_CLIENT });
  render(
    <Provider store={Store}>
      <BrowserRouter basename={`/${APP_BASE_URL_NAME}`}>
        <AppV2 />
      </BrowserRouter>
    </Provider>,
    document.getElementById('root'),
  );
};

window.notificationQueue = {};
window.pushGAEvent = data => {
  window.dataLayer.push(data);
};

window
  .fetch(require('config.json'))
  .then(async configResponse => {
    if (configResponse.status !== 200) return Promise.reject(configResponse);
    window.Grab.config = await configResponse.json();
    await getUserFeatureFlagsAsync();
    const locale = getLocale();
    I18n.locale = locale;
    return Promise.all(
      // locale lazy load (with "en" as fallback)
      [locale, 'en'].map(localeId =>
        window
          .fetch(require(`public/locales/${localeId}.json`))
          .then(async localeResponse => {
            if (localeResponse.status !== 200) return;
            I18n.translations[localeId] = await localeResponse.json();
          }),
      ),
    );
  })
  .then(() => {
    if (!isDev()) {
      const config = ddRum.buildDefaultConfig({
        applicationId: '3def6a45-a0e0-4e88-9ee5-deea1b70db3e',
        clientToken: 'pub78185d2b705b2afc4bece6ba38722981',
        service: window.Grab.config.appId.toLowerCase(),
        env: window.Grab.config.env,
        version: process.env.APP_VERSION,
        beforeSend: (event, context) => {
          if (event.type === 'error' && event.error) {
            const errorPatternsToIgnore = [
              /zaloJSV2/i,
              /Script error\./i,
              /service-worker\.js load failed/i,
              /NetworkError when attempting to fetch resource/i,
              /The operation was aborted/i,
              /The user aborted a request/i,
            ];

            const isIgnoreError = errorPatternsToIgnore.some(pattern => pattern.test(event.error.message));

            if (!isProduction()) {
              const logPrefix = isIgnoreError ? '[RUM] Ignored error:' : '[RUM] Error:';
              console.log(logPrefix, event, context);
            }

            if (isIgnoreError) {
              return false; // Return false to discard the event
            }
          }

          return event;
        },
      });
      ddRum.init(config);
    }
    renderAppV2();
  })
  .catch(err => {
    const errorCtx = rumErrorCtx()
      .component('AppInitialization')
      .message('Failed to initialize application')
      .severity(SEVERITY.CRITICAL)
      .build();
    ddRum.addError(err, errorCtx);
    // eslint-disable-next-line no-console
    console.error(err);
  });

if (module.hot) {
  // TODO: should this change to './AppV2' ?
  module.hot.accept('./App', () => {
    renderAppV2();
  });
}

export default {};
